import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Suspense, lazy } from "react";
import { usePageTracking } from "@/hooks/usePageTracking";
import { LanguageProvider } from "@/i18n/LanguageContext";
import { HreflangTags } from "@/components/HreflangTags";
import { AuthProvider } from "@/hooks/useAuth";
import LoadingScreen from "@/components/LoadingScreen";
import ProtectedRoute from "@/components/ProtectedRoute";
import NewsletterPopup from "@/components/NewsletterPopup";
import { RedditPixelInit } from "@/components/RedditPixelInit";
import { useHashScroll } from "@/hooks/useHashScroll";
import '@/i18n/config';

const Index = lazy(() => import("./pages/Index"));
const Auth = lazy(() => import("./pages/Auth"));
const FounderTiers = lazy(() => import("./pages/FounderTiers"));
const EliteTiers = lazy(() => import("./pages/EliteTiers"));
const Enterprise = lazy(() => import("./pages/Enterprise"));
const EnterpriseContact = lazy(() => import("./pages/EnterpriseContact"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const PainPoints = lazy(() => import("./pages/PainPoints"));
const EnterpriseCalm = lazy(() => import("./pages/EnterpriseCalm"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Founders = lazy(() => import("./pages/Founders"));
const Terms = lazy(() => import("./pages/Terms"));
const NotFound = lazy(() => import("./pages/NotFound"));
const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const About = lazy(() => import("./pages/About"));
const Launch = lazy(() => import("./pages/Launch"));
const Admin = lazy(() => import("./pages/Admin"));
const FreeTrial = lazy(() => import("./pages/FreeTrial"));
const FAQ = lazy(() => import("./pages/FAQ"));
const SignUp = lazy(() => import("./pages/SignUp"));
 const Feedback = lazy(() => import("./pages/Feedback"));

const queryClient = new QueryClient();

const PageTracker = () => {
  usePageTracking();
  useHashScroll();
  return null;
};

// Define all main routes
const mainRoutes = [
  { path: "/", element: <Index /> },
  { path: "/auth", element: <Auth /> },
  { path: "/founder-tiers", element: <ProtectedRoute><FounderTiers /></ProtectedRoute> },
  { path: "/elite-tiers", element: <EliteTiers /> },
  { path: "/enterprise", element: <Enterprise /> },
  { path: "/enterprise/contact", element: <EnterpriseContact /> },
  { path: "/blog", element: <Blog /> },
  { path: "/blog/:slug", element: <BlogPost /> },
  { path: "/free-trial", element: <FreeTrial /> },
  { path: "/pain-points", element: <PainPoints /> },
  { path: "/calm", element: <EnterpriseCalm /> },
  { path: "/privacy", element: <Privacy /> },
  { path: "/founders", element: <Founders /> },
  { path: "/terms", element: <Terms /> },
  { path: "/how-it-works", element: <HowItWorks /> },
  { path: "/about", element: <About /> },
  { path: "/launch", element: <Launch /> },
  { path: "/admin", element: <Admin /> },
  { path: "/faq", element: <FAQ /> },
  { path: "/signup", element: <SignUp /> },
   { path: "/feedback", element: <Feedback /> },
];

// Language prefixes for non-English languages
const languagePrefixes = ['es', 'fr', 'de', 'ja', 'ko', 'zh', 'nl', 'pt', 'it', 'ar'];

const AppContent = () => {
  return (
    <LanguageProvider>
      <PageTracker />
      <RedditPixelInit />
      <HreflangTags />
      <Suspense fallback={<LoadingScreen />}>
        <Routes>
          {/* English routes (default, no prefix) */}
          {mainRoutes.map((route) => (
            <Route key={route.path} path={route.path} element={route.element} />
          ))}
          
          {/* Language-prefixed routes */}
          {languagePrefixes.map((lang) => (
            mainRoutes.map((route) => {
              const prefixedPath = route.path === "/" 
                ? `/${lang}` 
                : `/${lang}${route.path}`;
              return (
                <Route 
                  key={`${lang}-${route.path}`} 
                  path={prefixedPath} 
                  element={route.element} 
                />
              );
            })
          ))}
          
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </LanguageProvider>
  );
};

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <NewsletterPopup />
          <BrowserRouter>
            <AppContent />
          </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;
